/*
 * <reviews-carousel> runs a single Flickity carousel of review cards at every
 * viewport width (unlike <custom-content-slider> / <text-image-rows-mobile-carousel>,
 * which each only run at one side of a breakpoint) since there is only one
 * flat row of cards here, not a layout that changes shape between mobile and
 * desktop. Cells are centered at every width, matching the Figma reference
 * (the active card sits centered with neighboring cards peeking in on both
 * sides).
 */
if (!customElements.get('reviews-carousel')) {
  class ReviewsCarousel extends HTMLElement {
    constructor() {
      super();
      this.resizeSliderEvent = () => this.resizeSlider();
      // section-reviews.css loads via a preloaded <link> that only becomes
      // an active stylesheet once it finishes (see the onload handler in
      // sections/section-reviews.liquid, which dispatches this event on the
      // document). This script itself runs earlier (deferred, but not
      // waiting on that stylesheet), so Flickity's very first layout pass
      // can happen before the carousel's real widths/gaps are in effect —
      // it renders using whatever un-styled dimensions existed at that
      // moment, and nothing fixes it up afterwards on its own. theme.js also
      // redispatches this same event on every real window resize, so this
      // one listener covers both cases.
      this.resizeOnCssLoad = () => this.resizeSlider();
      this.flkty = null;
    }

    connectedCallback() {
      this.initSlider();
      document.addEventListener('theme:resize:width', this.resizeOnCssLoad);
      this.addEventListener('theme:slider:resize', this.resizeSliderEvent);
    }

    initSlider() {
      if (this.flkty !== null) return;
      if (this.querySelectorAll('[data-slide]').length > 1) {
        this.flkty = new window.theme.Flickity(this, {
          prevNextButtons: true,
          pageDots: false,
          wrapAround: true,
          adaptiveHeight: false,
          cellAlign: 'center',
        });
      }
    }

    resizeSlider() {
      if (this.flkty !== null) {
        this.flkty.resize();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('theme:resize:width', this.resizeOnCssLoad);
      this.removeEventListener('theme:slider:resize', this.resizeSliderEvent);
      if (this.flkty !== null) {
        this.flkty.destroy();
        this.flkty = null;
      }
    }
  }

  customElements.define('reviews-carousel', ReviewsCarousel);
}
